import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';
import { useStatsig } from '@atlassian/mpac-feature-flags';
import { BodySmall, H6, LinkWithIcon } from '@atlassian/mpac-primitives';
import { useHistory } from '@atlassian/mpac-ui';
import { styled } from '@compiled/react';
import React from 'react';

import { EyebrowText } from '../ExpandedMenus.styles';
import { HelpfulLinks } from '../HelpfulLinks';

export const Container = styled.div({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  marginTop: token('space.300', '24px'),
  height: '100%',
  [media.above.md]: {
    marginTop: 0,
    marginBottom: 0,
  },
  // Extra margin to make bottom content easily reachable on mobile/tablet while scrolling
  marginBottom: '240px',
});

const WhatsNewSection = styled.div({
  marginBottom: token('space.400', '32px'),

  '& h3': {
    marginTop: token('space.200', '16px'),
  },
});

const Description = styled(BodySmall)({
  margin: `${token('space.050', '4px')} 0 ${token('space.100', '8px')} 0`,
});

export const WhatsNew = ({ closeMenu }: { closeMenu: () => void }) => {
  const { showCompassApps, showTeamworkGraphConnectors, enableAppificationChanges } = useStatsig();
  const history = useHistory();
  const navigationLinkClickHandler = (event) => {
    event.preventDefault();
    history?.push('/product/compass');
    closeMenu();
  };
  const teamworkGraphConnectorsClickHandler = (event) => {
    event.preventDefault();
    history?.push('/collections/teamwork-graph');
    closeMenu();
  };
  const atlassianTeamDescription = enableAppificationChanges()
    ? 'Be the first to experience our latest Atlassian app innovations in action, connect with industry leaders, and discover new ways to level up your work.'
    : 'Be the first to see our latest product advancements in action, network with industry greats, and learn how to level up your work.';
  return (
    <Container>
      <WhatsNewSection>
        <EyebrowText role="heading" aria-level={2}>
          What's new
        </EyebrowText>
        {showTeamworkGraphConnectors() && (
          <>
            <H6 as="h3">Teamwork Graph connectors</H6>
            <Description>
              Connect data from your external tools to power your Atlassian experiences.
            </Description>
            <LinkWithIcon
              size="small"
              type="internal"
              href="/collections/teamwork-graph"
              onClick={(event) => teamworkGraphConnectorsClickHandler(event)}
              aria-label="Explore new teamwork graph connectors"
            >
              Explore now
            </LinkWithIcon>
          </>
        )}
        {showCompassApps() && (
          <>
            <H6 as="h3">Compass apps</H6>
            <Description>
              Upgrade to the ultimate developer experience with apps for Compass that enable unified
              implementation in days.
            </Description>
            <LinkWithIcon
              size="small"
              type="internal"
              href="/product/compass"
              onClick={(event) => navigationLinkClickHandler(event)}
              aria-label="Explore new compass apps"
            >
              Explore now
            </LinkWithIcon>
          </>
        )}
        <H6 as="h3">Atlassian Team '25</H6>
        <Description>{atlassianTeamDescription}</Description>
        <LinkWithIcon
          size="small"
          type="external"
          href="https://events.atlassian.com/team-digital"
          onClick={closeMenu}
          aria-label="Watch highlights Atlassian team '25"
        >
          Watch highlights
        </LinkWithIcon>
      </WhatsNewSection>
      <HelpfulLinks closeMenu={closeMenu} />
    </Container>
  );
};
